/**
 * Nova theme behaviours.
 *
 * Everything is delegated and re-initialised on Shopify's section lifecycle
 * events, so the theme editor stays interactive when a merchant edits or
 * reorders a section.
 */
(function () {
  'use strict';

  var ICON_PLUS =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true"><path d="M2.9978 10H16.9987" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 3V17.0009" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_MINUS =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true"><path d="M2.9978 10H16.9987" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var DESKTOP = '(min-width: 1000px)';

  function each(root, selector, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(selector), fn);
  }

  /* ---------------------------------------------------------------- accordions
     Handles the FAQ list, the product detail stack and the mobile
     targeted-support accordion. `data-multi-open` decides whether opening one
     row closes the others. */
  function setRow(trigger, open) {
    var panel = trigger.parentElement.querySelector('[data-nova-accordion-panel]');
    var icon = trigger.querySelector('[data-nova-accordion-icon]');
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) {
      panel.classList.toggle('grid-rows-[1fr]', open);
      panel.classList.toggle('opacity-100', open);
      panel.classList.toggle('grid-rows-[0fr]', !open);
      panel.classList.toggle('opacity-0', !open);
    }
    if (icon) icon.innerHTML = open ? ICON_MINUS : ICON_PLUS;
  }

  function initAccordions(root) {
    each(root, '[data-nova-accordion]', function (list) {
      if (list.dataset.novaReady) return;
      list.dataset.novaReady = '1';
      var multi = list.dataset.multiOpen !== 'false';

      list.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-nova-accordion-trigger]');
        if (!trigger || !list.contains(trigger)) return;
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (!multi) {
          each(list, '[data-nova-accordion-trigger]', function (other) {
            if (other !== trigger) setRow(other, false);
          });
        }
        setRow(trigger, !isOpen);
      });
    });
  }

  /* ------------------------------------------------------- targeted-support tabs
     The origin binds BOTH mouseenter and click, so the panel follows the
     pointer on desktop and click still works for touch and keyboards. */
  function initTabs(root) {
    each(root, '[data-nova-tabs]', function (group) {
      if (group.dataset.novaReady) return;
      group.dataset.novaReady = '1';

      var tabs = group.querySelectorAll('[data-nova-tab]');
      var panels = group.querySelectorAll('[data-nova-tab-panel]');

      function activate(index) {
        Array.prototype.forEach.call(tabs, function (tab, i) {
          tab.classList.toggle('opacity-100', i === index);
          tab.classList.toggle('opacity-40', i !== index);
        });
        Array.prototype.forEach.call(panels, function (panel, i) {
          panel.classList.toggle('hidden', i !== index);
          if (i === index) {
            panel.classList.remove('nova-panel-in');
            void panel.offsetWidth; // restart the entrance animation
            panel.classList.add('nova-panel-in');
          }
        });
      }

      Array.prototype.forEach.call(tabs, function (tab, i) {
        tab.addEventListener('mouseenter', function () { activate(i); });
        tab.addEventListener('click', function () { activate(i); });
        tab.addEventListener('focus', function () { activate(i); });
      });
    });
  }

  /* ------------------------------------------------------------ timeline tabs */
  function initTimeline(root) {
    each(root, '[data-nova-timeline-track]', function (track) {
      if (track.dataset.novaReady) return;
      track.dataset.novaReady = '1';

      var section = track.closest('.shopify-section, section') || document;
      var tabs = section.querySelectorAll('[data-nova-timeline-tab]');
      var prev = section.querySelector('[data-nova-timeline-prev]');
      var next = section.querySelector('[data-nova-timeline-next]');
      var index = 0;

      function paint() {
        Array.prototype.forEach.call(tabs, function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.classList.toggle('opacity-100', on);
          tab.classList.toggle('opacity-45', !on);
          tab.style.borderColor = on ? 'currentColor' : 'rgba(29,59,19,.2)';
        });
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index >= track.children.length - 1;
      }

      function goTo(i) {
        index = Math.max(0, Math.min(track.children.length - 1, i));
        var card = track.children[index];
        if (card) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        paint();
      }

      Array.prototype.forEach.call(tabs, function (tab, i) {
        tab.addEventListener('click', function () { goTo(i); });
      });
      if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
      if (next) next.addEventListener('click', function () { goTo(index + 1); });
      paint();
    });
  }

  /* --------------------------------------------------------- generic carousel */
  function initCarousels(root) {
    each(root, '[data-nova-carousel]', function (track) {
      if (track.dataset.novaReady) return;
      track.dataset.novaReady = '1';
      var section = track.closest('.shopify-section, section') || document;
      var prev = section.querySelector('[data-nova-carousel-prev]');
      var next = section.querySelector('[data-nova-carousel-next]');
      var step = function () {
        var first = track.children[0];
        return first ? first.getBoundingClientRect().width + 16 : 320;
      };
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    });
  }

  /* -------------------------------------------------------------- video tiles
     Click to play; starting one pauses any other that is running. */
  function initVideos(root) {
    each(root, '[data-nova-video-row]', function (row) {
      if (row.dataset.novaReady) return;
      row.dataset.novaReady = '1';

      row.addEventListener('click', function (event) {
        var tile = event.target.closest('[data-nova-video-tile]');
        if (!tile) return;
        var video = tile.querySelector('video');
        if (!video) return;

        each(row, 'video', function (other) { if (other !== video) other.pause(); });
        if (video.paused) { video.play(); } else { video.pause(); }
      });

      each(row, 'video', function (video) {
        var badge = video.parentElement.querySelector('[data-nova-video-play]');
        if (!badge) return;
        video.addEventListener('play', function () { badge.classList.add('hidden'); });
        video.addEventListener('pause', function () { badge.classList.remove('hidden'); });
      });
    });
  }

  /* ----------------------------------------------------------- read-more clamp */
  function initReadMore(root) {
    each(root, '[data-nova-readmore]', function (wrap) {
      if (wrap.dataset.novaReady) return;
      wrap.dataset.novaReady = '1';
      var toggle = wrap.querySelector('[data-nova-readmore-toggle]');
      var text = wrap.querySelector('[data-nova-readmore-text]');
      if (!toggle || !text) return;

      toggle.addEventListener('click', function () {
        var expanded = text.classList.toggle('line-clamp-none');
        text.classList.toggle('line-clamp-1', !expanded);
        toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------ product gallery */
  function initGallery(root) {
    each(root, '[data-nova-gallery]', function (gallery) {
      if (gallery.dataset.novaReady) return;
      gallery.dataset.novaReady = '1';

      var main = gallery.querySelector('[data-nova-gallery-main]');
      var strip = gallery.querySelector('[data-nova-gallery-strip]');
      var thumbs = gallery.querySelectorAll('[data-nova-gallery-thumb]');
      var index = 0;

      function show(i) {
        if (!thumbs.length) return;
        index = (i + thumbs.length) % thumbs.length;
        var src = thumbs[index].getAttribute('data-nova-gallery-thumb');
        if (main && src) {
          main.src = src;
          main.removeAttribute('srcset');
        }
      }

      Array.prototype.forEach.call(thumbs, function (thumb, i) {
        thumb.addEventListener('click', function () { show(i); });
      });

      /* Swipe on the image itself. The arrows are desktop-only, so on a phone
         there was no way at all to reach the second photograph.

         Horizontal intent is decided before anything is blocked: a mostly
         vertical drag stays a page scroll, which is what a thumb does most of
         the time. */
      if (main) {
        var startX = 0, startY = 0, tracking = false, horizontal = false;

        main.addEventListener('touchstart', function (e) {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          tracking = true;
          horizontal = false;
        }, { passive: true });

        main.addEventListener('touchmove', function (e) {
          if (!tracking) return;
          var dx = e.touches[0].clientX - startX;
          var dy = e.touches[0].clientY - startY;
          if (!horizontal && Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) horizontal = true;
          if (horizontal && e.cancelable) e.preventDefault();
        }, { passive: false });

        main.addEventListener('touchend', function (e) {
          if (!tracking) return;
          tracking = false;
          if (!horizontal) return;
          var dx = (e.changedTouches[0] || {}).clientX - startX;
          if (Math.abs(dx) < 40) return;          // a tap or a twitch, not a swipe
          show(dx < 0 ? index + 1 : index - 1);   // drag left moves forward
        });
      }

      var prev = gallery.querySelector('[data-nova-gallery-prev]');
      var next = gallery.querySelector('[data-nova-gallery-next]');
      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });

      var sPrev = gallery.querySelector('[data-nova-gallery-strip-prev]');
      var sNext = gallery.querySelector('[data-nova-gallery-strip-next]');
      var stripStep = function () { return strip ? (strip.clientWidth - 32) / 5 + 8 : 80; };
      if (sPrev) sPrev.addEventListener('click', function () { strip.scrollBy({ left: -stripStep(), behavior: 'smooth' }); });
      if (sNext) sNext.addEventListener('click', function () { strip.scrollBy({ left: stripStep(), behavior: 'smooth' }); });
    });
  }

  /* ------------------------------------------------------------- bundle picker
     Each row carries a variant id; selecting one rewrites the form's hidden
     `id` input so Add to cart posts the right variant. */
  function initBundles(root) {
    each(root, '[data-nova-bundles]', function (group) {
      if (group.dataset.novaReady) return;
      group.dataset.novaReady = '1';

      var form = group.closest('form');
      var variantInput = form ? form.querySelector('[data-nova-variant-input]') : null;

      function select(row) {
        each(group, '[data-nova-bundle]', function (other) {
          var on = other === row;
          var input = other.querySelector('[data-nova-bundle-input]');
          var radio = other.querySelector('[data-nova-bundle-radio]');
          var dot = other.querySelector('[data-nova-bundle-dot]');
          if (input) input.checked = on;
          other.classList.toggle('border-black', on);
          other.classList.toggle('bg-white', on);
          other.classList.toggle('border-black/30', !on);
          other.classList.toggle('bg-[#f2f2f2]', !on);
          if (radio) {
            radio.classList.toggle('border-black', on);
            radio.classList.toggle('border-black/40', !on);
          }
          if (dot) dot.classList.toggle('hidden', !on);
        });

        var chosen = row.querySelector('[data-nova-bundle-input]');
        if (variantInput && chosen && chosen.value) variantInput.value = chosen.value;
      }

      group.addEventListener('click', function (event) {
        var row = event.target.closest('[data-nova-bundle]');
        if (row) select(row);
      });

      var initial = group.querySelector('[data-nova-bundle-input]:checked');
      if (initial) select(initial.closest('[data-nova-bundle]'));
    });
  }

  /* ------------------------------------------------------------- footer links */
  function initFooterLinks(root) {
    each(root, '[data-nova-footer-links]', function (wrap) {
      if (wrap.dataset.novaReady) return;
      wrap.dataset.novaReady = '1';
      var toggle = wrap.querySelector('[data-nova-footer-toggle]');
      var panel = wrap.querySelector('[data-nova-footer-panel]');
      var icon = wrap.querySelector('[data-nova-footer-icon]');
      if (!toggle || !panel) return;

      toggle.addEventListener('click', function () {
        if (window.matchMedia(DESKTOP).matches) return; // always open on desktop
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.classList.toggle('grid-rows-[1fr]', open);
        panel.classList.toggle('opacity-100', open);
        panel.classList.toggle('grid-rows-[0fr]', !open);
        panel.classList.toggle('opacity-0', !open);
        if (icon) icon.innerHTML = open ? ICON_MINUS : ICON_PLUS;
      });
    });
  }

  /* ------------------------------------------------------------- nav drawer */
  function initNav(root) {
    each(root, '[data-nova-menu]', function (menu) {
      if (menu.dataset.novaReady) return;
      menu.dataset.novaReady = '1';
      var header = menu.closest('header') || document;
      var openBtn = header.querySelector('[data-nova-menu-toggle]');
      var closeBtn = header.querySelector('[data-nova-menu-close]');
      var overlay = header.querySelector('[data-nova-menu-overlay]');

      function set(open) {
        menu.dataset.open = open ? 'true' : 'false';
        if (overlay) overlay.dataset.open = open ? 'true' : 'false';
        document.body.style.overflow = open ? 'hidden' : '';
      }
      if (openBtn) openBtn.addEventListener('click', function () { set(true); });
      if (closeBtn) closeBtn.addEventListener('click', function () { set(false); });
      if (overlay) overlay.addEventListener('click', function () { set(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') set(false); });
    });
  }

  /* ------------------------------------------------------------- countdown
     Three modes: `daily` resets at local midnight so an evergreen offer never
     shows an expired timer, `rolling` gives each visitor their own window
     persisted in localStorage, `fixed` counts to a set date and then hides the
     bar entirely rather than sitting at zero. */
  function initCountdown(root) {
    each(root, '[data-nova-countdown]', function (el) {
      if (el.dataset.novaReady) return;
      el.dataset.novaReady = '1';

      var mode = el.dataset.mode || 'daily';
      var bar = el.closest('[data-nova-announcement]');
      var cells = {};
      each(el, '[data-cd]', function (c) { cells[c.getAttribute('data-cd')] = c; });

      function target() {
        var now = new Date();
        if (mode === 'fixed') {
          var t = Date.parse(el.dataset.end);
          return isNaN(t) ? null : t;
        }
        if (mode === 'rolling') {
          var hours = parseFloat(el.dataset.hours) || 24;
          var key = 'nova-countdown-' + hours;
          var stored = null;
          try { stored = window.localStorage.getItem(key); } catch (e) {}
          var end = stored ? parseInt(stored, 10) : NaN;
          if (!end || isNaN(end) || end <= now.getTime()) {
            end = now.getTime() + hours * 3600 * 1000;
            try { window.localStorage.setItem(key, String(end)); } catch (e) {}
          }
          return end;
        }
        // daily — next local midnight
        var midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        return midnight.getTime();
      }

      var end = target();
      if (end === null) return;

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          if (mode === 'fixed') {
            if (bar) bar.hidden = true;
            return;
          }
          end = target();                 // roll over to the next window
          left = Math.max(0, end - Date.now());
        }
        var secs = Math.floor(left / 1000);
        var days = Math.floor(secs / 86400);
        var hours = Math.floor((secs % 86400) / 3600);
        if (!cells.days) hours += days * 24;   // fold days in when hidden
        if (cells.days) cells.days.textContent = pad(days);
        if (cells.hours) cells.hours.textContent = pad(hours);
        if (cells.minutes) cells.minutes.textContent = pad(Math.floor((secs % 3600) / 60));
        if (cells.seconds) cells.seconds.textContent = pad(secs % 60);
      }

      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---- Results band: count figures up when they scroll into view ---------- */
  function initCounters(root) {
    var nodes = root.querySelectorAll('[data-nova-count]');
    if (!nodes.length) return;

    // Reduced-motion users get the final value with no animation at all.
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function run(el) {
      var target = parseFloat(el.getAttribute('data-count-to'));
      if (isNaN(target)) return;
      // Match the target's own precision so 4.8 does not land on 5.
      var dp = (el.getAttribute('data-count-to').split('.')[1] || '').length;
      // Group thousands, or 10839 reads as a part number rather than a count.
      var fmt = function (n) {
        var s = n.toFixed(dp).split('.');
        s[0] = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return s.join('.');
      };
      var start = null, dur = 1400;
      function frame(now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = fmt(target * eased);
        if (t < 1) requestAnimationFrame(frame);
        else el.textContent = fmt(target);
      }
      requestAnimationFrame(frame);
    }

    Array.prototype.forEach.call(nodes, function (el) {
      if (el.hasAttribute('data-nova-counted')) return;
      el.setAttribute('data-nova-counted', '');
      if (still || !('IntersectionObserver' in window)) {
        var raw = el.getAttribute('data-count-to');
        el.textContent = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return;
      }
      el.textContent = '0';
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          run(el);
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---- Limited restock: fill the bar rather than state it --------------- */
  function initRestock(root) {
    each(root, '[data-nova-restock]', function (track) {
      if (track.dataset.novaReady) return;
      track.dataset.novaReady = '1';

      var fill = track.querySelector('[data-nova-restock-fill]');
      var value = track.querySelector('[data-nova-restock-value]');
      var target = parseFloat(track.getAttribute('data-nova-restock'));
      if (!fill || isNaN(target)) return;

      var settle = function () {
        fill.style.width = target + '%';
        if (value) value.textContent = String(Math.round(target));
      };

      // Reduced motion, or no observer to hang it on: state the figure.
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (still || !('IntersectionObserver' in window)) { settle(); return; }

      fill.style.width = '0%';
      if (value) value.textContent = '0';

      function run() {
        var start = null, dur = 1600;
        function frame(now) {
          if (start === null) start = now;
          var t = Math.min(1, (now - start) / dur);
          var eased = 1 - Math.pow(1 - t, 3);
          fill.style.width = (target * eased) + '%';
          if (value) value.textContent = String(Math.round(target * eased));
          if (t < 1) requestAnimationFrame(frame);
          else settle();
        }
        requestAnimationFrame(frame);
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          run();
        });
      }, { threshold: 0.4 });
      io.observe(track);
    });
  }

  /* ---- Customer reviews: Show more, plus filter by star rating ---------- */
  function initReviews(root) {
    each(root, '[data-nova-reviews]', function (list) {
      if (list.dataset.novaReady) return;
      list.dataset.novaReady = '1';

      var section = list.closest('.shopify-section, section') || document;
      var cards = list.querySelectorAll('[data-nova-review]');
      var btn = section.querySelector('[data-nova-reviews-more]');
      var note = section.querySelector('[data-nova-filter-note]');
      var noteText = section.querySelector('[data-nova-filter-text]');
      var clear = section.querySelector('[data-nova-filter-clear]');
      var limit = parseInt(list.getAttribute('data-visible'), 10) || 9;
      var filter = 0;   // 0 = all
      var expanded = false;

      function paint() {
        var shown = 0;
        Array.prototype.forEach.call(cards, function (card) {
          var matches = !filter || card.getAttribute('data-rating') === String(filter);
          // While filtering, show every match: a filtered list is short enough
          // that paging it again just hides what the visitor asked to see.
          var visible = matches && (filter || expanded || shown < limit);
          if (visible) shown++;
          card.classList.toggle('hidden', !visible);
        });
        if (btn) {
          var total = 0;
          Array.prototype.forEach.call(cards, function (c) {
            if (!filter || c.getAttribute('data-rating') === String(filter)) total++;
          });
          btn.classList.toggle('hidden', !!filter || total <= limit);
          btn.textContent = expanded ? btn.getAttribute('data-less') : btn.getAttribute('data-more');
        }
        if (note) {
          note.classList.toggle('hidden', !filter);
          note.classList.toggle('flex', !!filter);
          if (filter && noteText) {
            noteText.textContent = (note.getAttribute('data-template') || 'Showing {n} star reviews')
              .replace('{n}', filter);
          }
        }
      }

      each(section, '[data-nova-star-filter]', function (bar) {
        bar.addEventListener('click', function () {
          var star = parseInt(bar.getAttribute('data-nova-star-filter'), 10);
          filter = filter === star ? 0 : star;
          expanded = false;
          paint();
          list.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
      });

      if (clear) clear.addEventListener('click', function () { filter = 0; expanded = false; paint(); });
      if (btn) btn.addEventListener('click', function () {
        expanded = !expanded;
        paint();
        if (!expanded) btn.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });

      paint();
    });
  }

  /* ---- Empty the bag before adding, so nothing can stack up ------------- */
  function initReplaceCart(root) {
    each(root, 'form[data-nova-replace-cart]', function (form) {
      if (form.dataset.novaReady) return;
      form.dataset.novaReady = '1';

      form.addEventListener('submit', function (event) {
        if (form.dataset.novaCleared === '1') return;   // second pass, let it go
        event.preventDefault();

        // There is no cart page on this store, so a shopper who adds twice has
        // nowhere to remove the extra line. Clearing first means the bag always
        // holds exactly what was chosen last, and an accidental second click
        // replaces rather than accumulates.
        var done = function () {
          form.dataset.novaCleared = '1';
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        };

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/clear.js' : '/cart/clear.js',
              { method: 'POST', headers: { 'Content-Type': 'application/json' } })
          .then(done)
          // A failed clear must not block the sale: fall through and add anyway.
          .catch(done);
      });
    });
  }

  /* ---- Cart drawer ------------------------------------------------------
     Lines are drawn from /cart.js rather than Liquid so the panel can redraw
     after a quantity change without a page load. The free gift is a real
     variant carrying a `_gift` property; any line with that property is shown
     under the mystery-gift label instead of the product's own name, so the
     promise stays a surprise while the fulfilment stays honest. */
  function initCartDrawer(root) {
    var drawer = (root || document).querySelector('[data-nova-drawer]');
    if (!drawer || drawer.dataset.novaReady) return;
    drawer.dataset.novaReady = '1';

    var panel = drawer.querySelector('[data-nova-drawer-panel]');
    var overlay = drawer.querySelector('[data-nova-drawer-overlay]');
    var linesEl = drawer.querySelector('[data-nova-drawer-lines]');
    var subtotalEl = drawer.querySelector('[data-nova-drawer-subtotal]');
    var countEl = drawer.querySelector('[data-nova-drawer-count]');
    var progressEl = drawer.querySelector('[data-nova-progress]');
    var savingEl = drawer.querySelector('[data-nova-saving]');
    var checkoutEl = drawer.querySelector('[data-nova-drawer-checkout]');
    var addonEl = drawer.querySelector('[data-nova-addon]');
    var addonVariant = (drawer.dataset.addonVariant || '').trim();
    var addonOnColour = drawer.dataset.addonOn || '#ff5781';

    /* The add-on is marked so it can be told apart from a normal line: the
       shopper toggles it here, and it must not be counted as one of the items
       they chose or leave a stray line the switch cannot find again. */
    var isAddon = function (item) {
      return !!(item.properties && item.properties._addon) ||
             (addonVariant && String(item.variant_id) === addonVariant);
    };

    /* The store's own free gift (a £0 variant). Like the add-on it is not one
       of the shopper's choices: it is added beside a product on the pages
       that offer it, labelled as a gift, and leaves with the last product. */
    var giftVariant = (drawer.dataset.giftVariant || '').trim();
    var giftTried = 0;
    var giftPending = false;
    var isFreeGift = function (item) {
      return !!(item.properties && item.properties._gift) ||
             (giftVariant && String(item.variant_id) === giftVariant);
    };

    // Live price for the add-on, so the row cannot advertise a stale figure.
    if (addonVariant && drawer.dataset.addonHandle) {
      fetch('/products/' + drawer.dataset.addonHandle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (prod) {
          var v = (prod.variants || []).filter(function (x) { return String(x.id) === addonVariant; })[0];
          var priceEl = drawer.querySelector('[data-nova-addon-price]');
          if (v && priceEl) priceEl.textContent = money(v.price);
          var imgEl = drawer.querySelector('[data-nova-addon-image]');
          if (v && imgEl && prod.featured_image) {
            imgEl.outerHTML = '<img src="' + prod.featured_image + '" alt="" class="size-[46px] shrink-0 rounded-[6px] object-cover">';
          }
        })
        .catch(function () {});
    }

    var addonAutoTried = 0;
    var addonPending = false;

    function paintAddon(cart) {
      if (!addonEl) return;
      var on = cart.items.some(isAddon);

      /* Pre-selected extras are added once per visit, never re-added after the
         shopper switches it off — otherwise the switch would not work. */
      if (!on && addonAutoTried < 3 && !addonPending && addonVariant &&
          drawer.dataset.addonDefault === 'true' && cart.items.length &&
          !sessionStorage.getItem('novaAddonDeclined')) {
        // Retried like the gift: an add racing the bundle app can be lost.
        addonAutoTried += 1;
        addonPending = true;
        api('/cart/add.js', { items: [{ id: Number(addonVariant), quantity: 1, properties: { _addon: 'true' } }] })
          .catch(function () {})
          .then(function () {
            var check = function () {
              if (busy) { setTimeout(check, 500); return; }
              addonPending = false;
              refresh(false);
            };
            setTimeout(check, 1500);
          });
        return;
      }
      var track = addonEl.querySelector('[data-nova-addon-track]');
      var knob = addonEl.querySelector('[data-nova-addon-knob]');
      addonEl.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (track) track.style.background = on ? addonOnColour : '';
      if (knob) knob.style.transform = on ? 'translateX(22px)' : '';
    }

    if (addonEl) {
      addonEl.addEventListener('click', function () {
        if (busy) return;
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          var line = 0;
          cart.items.forEach(function (item, i) { if (isAddon(item)) line = i + 1; });
          if (line) {
            // Remember the refusal so the automatic add does not undo it.
            try { sessionStorage.setItem('novaAddonDeclined', '1'); } catch (e) {}
            addonAutoTried = 3;
            return api('/cart/change.js', { line: line, quantity: 0 });
          }
          try { sessionStorage.removeItem('novaAddonDeclined'); } catch (e) {}
          return api('/cart/add.js', { items: [{ id: Number(addonVariant), quantity: 1, properties: { _addon: 'true' } }] });
        }).then(function () { refresh(false); });
      });
    }
    var messageEl = drawer.querySelector('[data-nova-reward-message]');

    var giftAt = Math.round((parseFloat(drawer.dataset.giftThreshold) || 0) * 100);
    var shipAt = Math.round((parseFloat(drawer.dataset.shippingThreshold) || 0) * 100);
    var giftAddedEl = drawer.querySelector('[data-nova-gift-added]');
    var shipLabel = drawer.dataset.shippingLabel || 'Free Shipping';
    var giftLabel = drawer.dataset.giftLabel || 'Free Gift';

    var symbol = drawer.dataset.currency || '£';
    var busy = false;

    var money = function (cents) { return symbol + (cents / 100).toFixed(2); };

    /* The bundle app writes its deal onto the lines it owns. A bundle is a
       quantity at a price, so the two cannot be separated: changing the
       quantity here stops the deal matching, the bundle rate is lost and the
       gift that came with it starts being charged. The drawer reads these
       lines and leaves them alone. */
    var deal = function (item) {
      var raw = item.properties && item.properties.__kaching_bundles;
      if (!raw) return null;
      try { return JSON.parse(raw); } catch (e) { return {}; }
    };
    var isGift = function (item) { var d = deal(item); return !!(d && d.gift && !d.main); };
    // One bundle in the cart: the app's bundle instance, else its deal.
    var dealKey = function (d) { return d && (d.bid || d.deal || d.id) || ''; };

    /* cart.js carries no compare-at price, so the saving cannot be read from
       the cart alone. The product feed has it per variant; until it arrives the
       drawer simply shows no saving rather than a wrong one. */
    var compareAt = {};
    if (drawer.dataset.productHandle) {
      fetch('/products/' + drawer.dataset.productHandle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (prod) {
          (prod.variants || []).forEach(function (v) {
            if (v.compare_at_price && v.compare_at_price > v.price) compareAt[String(v.id)] = v.compare_at_price;
          });
          refresh(false);
        })
        .catch(function () {});
    }
    /* What the line would have cost. A discount applied by Shopify or by the
       bundle app shows up as an original price above the final one, which also
       covers a gift zeroed by an automatic discount; otherwise fall back to the
       variant's compare-at. Without this a free gift reads as £0.00 with no
       sign of what it is worth. */
    var wasPrice = function (item) {
      /* Two things can say what a line would have cost: the price before any
         discount the app applied, and the variant's compare-at. Take whichever
         is higher. Returning the first one found put a bundle's was-price at
         the sum of its own line prices — £73.08 against £72.99, a saving of
         9p — when the compare-at the product page advertises is £128.97. */
      var was = item.original_line_price > item.final_line_price ? item.original_line_price : 0;
      var c = compareAt[String(item.variant_id)];
      if (c && c * item.quantity > was) was = c * item.quantity;
      return was > item.final_line_price ? was : 0;
    };

    /* A line the bundle app has zeroed arrives with its original price zeroed
       too, so nothing in the cart says what the gift is worth. Its own product
       feed does. Fetched once per handle, then the drawer repaints. */
    var fetched = {};
    function learnValue(item) {
      var handle = item.handle;
      if (item.final_line_price !== 0 || !handle || fetched[handle]) return;
      fetched[handle] = true;
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (prod) {
          (prod.variants || []).forEach(function (v) {
            var worth = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
            if (worth > 0) compareAt[String(v.id)] = worth;
          });
          refresh(false);
        })
        .catch(function () {});
    }

    function open() {
      drawer.classList.remove('pointer-events-none');
      drawer.setAttribute('aria-hidden', 'false');
      overlay.classList.add('opacity-100');
      panel.classList.remove('translate-x-full');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.add('pointer-events-none');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('opacity-100');
      panel.classList.add('translate-x-full');
      document.body.style.overflow = '';
    }

    var api = function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) { return r.json(); });
    };

    /* Both rewards are earned on cart value. Gift lines are excluded from that
       value: a free line must not help pay for the threshold that gave it
       away, or the gift would keep itself once won. */
    function earned(cart) {
      var paid = cart.items.reduce(function (n, i) { return n + (isGift(i) ? 0 : i.final_line_price); }, 0);
      return { gift: giftAt > 0 && paid >= giftAt,
               shipping: shipAt > 0 && paid >= shipAt,
               paid: paid };
    }


    function render(cart) {
      var chosen = cart.items.filter(function (i) { return !isAddon(i) && !isFreeGift(i); });

      // A few tries, each checked against the cart a moment later: an add
      // made while the bundle app is still writing its lines can be lost.
      if (!chosen.length) giftTried = 0;
      if (chosen.length && giftVariant && drawer.dataset.giftAuto === 'true' && giftTried < 3 &&
          !giftPending && !cart.items.some(isFreeGift)) {
        giftTried += 1;
        giftPending = true;
        api('/cart/add.js', { items: [{ id: Number(giftVariant), quantity: 1, properties: { _gift: 'true' } }] })
          .catch(function () {})
          .then(function () {
            var check = function () {
              if (busy) { setTimeout(check, 500); return; }
              giftPending = false;
              refresh(false);
            };
            setTimeout(check, 1500);
          });
      }
      // One gift per order: trim a gift line that was raised above 1.
      var extraGift = cart.items.findIndex(function (i) { return isFreeGift(i) && i.quantity > 1; });
      if (extraGift > -1) {
        api('/cart/change.js', { line: extraGift + 1, quantity: 1 }).then(function () { refresh(false); });
      }
      var chosenCount = chosen.reduce(function (n, i) { return n + i.quantity; }, 0);

      /* The add-on is an extra on an order, not an order in itself. Left alone
         it would keep an empty bag looking full and offer a checkout with
         nothing being protected, so it goes when the last product does. */
      if (!chosen.length && cart.items.length) {
        api('/cart/clear.js', {}).then(function () { refresh(false); });
        return;
      }

      /* Repair a cart the old stepper left behind.

         A bundle line carries two independent claims: the variant it is,
         which names the deal, and the discount the app actually applied,
         which prices it. While the deal holds the two agree. Once the
         quantity stops matching, the app prices the line under a different
         deal and they part company — a line called Buy 2 + Get 1 FREE
         charged as a Single. There is no honest label for it: it is a single
         unit of a variant that only exists as a three-pack, so it is neither
         the bundle it names nor the single it was charged as. It goes, and
         the gift that came with it goes too.

         A gift being charged is likewise not a gift, whatever the drawer
         calls it, and was never chosen. Both are swept in one request. */
      var broken = {};
      cart.items.forEach(function (i) {
        var d = deal(i);
        if (!d || !d.main) return;
        var applied = (i.line_level_discount_allocations || []).map(function (a) {
          return ((a.discount_application || {}).title || '').trim().toLowerCase();
        });
        var named = (i.variant_title || '').trim().toLowerCase();
        // No discount at all is not a mismatch: the deal may simply give none.
        if (applied.length && named && applied.indexOf(named) === -1 && d.id) broken[d.id] = true;
      });
      var repair = {};
      cart.items.forEach(function (i) {
        var d = deal(i);
        if (d && d.id && broken[d.id]) repair[i.key] = 0;
        else if (isGift(i) && i.final_line_price > 0) repair[i.key] = 0;
      });
      if (Object.keys(repair).length) {
        api('/cart/update.js', { updates: repair }).then(function () { refresh(false); });
        return;
      }

      countEl.textContent = '(' + chosenCount + ')';
      each(document, '[data-nova-cart-badge]', function (b) {
        b.textContent = chosenCount;
        // Set outright rather than by class: the badge is inline-flex, which
        // wins over a hidden utility depending on stylesheet order.
        b.style.display = chosenCount === 0 ? 'none' : '';
      });
      subtotalEl.textContent = money(cart.total_price);

      // The total on the button removes one reason to hesitate: the shopper
      // knows what they are about to be charged before they leave the drawer.
      if (checkoutEl && checkoutEl.getAttribute('data-with-total') === 'true') {
        checkoutEl.textContent = checkoutEl.getAttribute('data-label') + ' • ' + money(cart.total_price);
      }

      // What the same bottles would have cost at the single-bottle rate, so the
      // bundle's advantage is stated rather than left to be worked out.
      // The free gift is left out, so the percentage is the saving on the bottles.
      var was = cart.items.reduce(function (n, i) { return n + (isAddon(i) || isFreeGift(i) ? 0 : Math.max(wasPrice(i), i.final_line_price)); }, 0);
      var saved = was - cart.items.reduce(function (n, i) { return n + (isAddon(i) || isFreeGift(i) ? 0 : i.final_line_price); }, 0);
      if (savingEl) {
        savingEl.classList.toggle('hidden', saved <= 0);
        savingEl.classList.toggle('flex', saved > 0);
        if (saved > 0) {
          savingEl.innerHTML = '<span>' + (drawer.dataset.savingLabel || 'You save') + '</span>' +
                               '<span class="font-bold">' + money(saved) + ' (' + Math.round((saved / was) * 100) + '%)</span>';
        }
      }

      var won = earned(cart);

      // The reward panel is optional: with no thresholds set it is not rendered.
      if (progressEl && messageEl) {
      /* The gift marker is pinned to the middle of the bar, so the fill is
         piecewise: value maps to the first half up to the gift and to the
         second half beyond it. A linear fill would put the marker wherever the
         arithmetic happened to land, which is not what a shopper reads. */
      var pct;
      if (!giftAt || !shipAt) {
        pct = shipAt ? Math.min(100, (won.paid / shipAt) * 100) : 0;
      } else if (won.paid <= giftAt) {
        pct = (won.paid / giftAt) * 50;
      } else {
        pct = 50 + ((won.paid - giftAt) / (shipAt - giftAt)) * 50;
      }
      progressEl.style.width = Math.max(0, Math.min(100, pct)) + '%';

      // Always name the next goal still to reach, not the one already passed.
      if (!won.gift && giftAt) {
        messageEl.textContent = money(giftAt - won.paid) + ' to go for ' + giftLabel;
      } else if (!won.shipping && shipAt) {
        messageEl.textContent = money(shipAt - won.paid) + ' to go for ' + shipLabel;
      } else {
        messageEl.textContent = shipLabel + ' unlocked!';
      }

      if (giftAddedEl) giftAddedEl.style.opacity = won.gift ? '1' : '0';

      each(drawer, '[data-nova-marker]', function (dot) {
        var hit = dot.getAttribute('data-nova-marker') === 'gift' ? won.gift : won.shipping;
        dot.style.background = hit ? getComputedStyle(progressEl).backgroundColor : '';
      });
      each(drawer, '[data-nova-marker-label]', function (label) {
        var hit = label.getAttribute('data-nova-marker-label') === 'gift' ? won.gift : won.shipping;
        label.style.opacity = hit ? '1' : '.5';
        label.style.color = hit ? getComputedStyle(progressEl).backgroundColor : '';
      });
      }


      paintAddon(cart);
      cart.items.forEach(learnValue);
      if (!chosen.length) {
        /* A refusal belongs to the bag it was made in. Once that bag is gone
           the shopper is starting again, so the extra is offered again rather
           than staying switched off for the rest of the visit — which is what
           made it look as though it was not on by default. */
        addonAutoTried = 0;
        try { sessionStorage.removeItem('novaAddonDeclined'); } catch (e) {}
        linesEl.innerHTML = '<p class="py-10 text-center text-[15px]" style="opacity:.6;">Your bag is empty.</p>';
        return;
      }

      /* Shopify can split one bundle across two lines of the same variant
         (an automatic discount, such as the free gift, claims one unit), so
         lines from the same deal and variant are shown as one. */
      var sameLine = function (a, b) {
        var da = deal(a), db = deal(b);
        return da && db && dealKey(da) && dealKey(da) === dealKey(db) && a.variant_id === b.variant_id &&
               !isAddon(b) && !isFreeGift(b) && isGift(a) === isGift(b);
      };
      linesEl.innerHTML = cart.items.map(function (item, i) {
        if (isAddon(item)) return '';
        if (cart.items.slice(0, i).some(function (o) { return sameLine(item, o); })) return '';
        var parts = cart.items.filter(function (o, j) { return j === i || (j > i && sameLine(item, o)); });
        var partsWas = parts.reduce(function (n, o) { return n + wasPrice(o); }, 0);
        if (parts.length > 1) {
          item = Object.assign({}, item, {
            quantity: parts.reduce(function (n, o) { return n + o.quantity; }, 0),
            final_line_price: parts.reduce(function (n, o) { return n + o.final_line_price; }, 0)
          });
        }
        var title = item.product_title;
        var d = deal(item);
        var ours = isFreeGift(item);
        var gift = isGift(item) || ours;
        // Removing a bundle removes everything the deal put in the cart.
        var keys = cart.items.filter(function (o) {
          var od = deal(o);
          return od && d && dealKey(od) && dealKey(od) === dealKey(d);
        }).map(function (o) { return o.key; });
        if (!keys.length) keys = [item.key];
        var price = item.final_line_price === 0 ? 'FREE' : money(item.final_line_price);
        var worth = parts.length > 1 ? partsWas : wasPrice(item);
        if (ours) worth = Math.round((parseFloat((drawer.dataset.giftValue || '').replace(/[^0-9.]/g, '')) || 0) * 100);
        var img = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '';
        return '' +
          '<div class="flex gap-3 py-4" style="border-bottom:1px solid var(--nova-line,#ffe1e9);">' +
            (img ? '<img src="' + img + '" alt="" class="size-[70px] shrink-0 rounded-[6px] object-cover">' : '') +
            '<div class="min-w-0 flex-1">' +
              '<div class="flex items-start justify-between gap-2">' +
                '<p class="text-[15px] font-bold">' + title + (ours ? ' <span style="display:inline-block;margin-left:6px;padding:1px 7px;border-radius:20px;background:#ff5781;color:#fff;font-size:10px;letter-spacing:.06em;vertical-align:2px;">GIFT</span>' : '') + '</p>' +
                (gift ? '' : '<button type="button" class="cursor-pointer text-[12px] underline" style="opacity:.55;" data-keys="' + keys.join(',') + '" data-remove>Remove</button>') +
              '</div>' +
              (gift ? '<p class="text-[12px]" style="opacity:.6;">' + (ours ? (drawer.dataset.giftNote || 'Free gift') : 'Free gift with your bundle') + '</p>'
                    : '<p class="text-[13px]" style="opacity:.6;">' + (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '') + '</p>') +
              '<div class="mt-2 flex items-center justify-between">' +
                (d || ours ? '<span class="text-[13px]" style="opacity:.6;">Qty ' + item.quantity + '</span>' :
                  '<span class="inline-flex items-center rounded-[4px]" style="border:1px solid var(--nova-line,#ffe1e9);">' +
                    '<button type="button" class="cursor-pointer px-2.5 py-1" data-line="' + (i + 1) + '" data-qty="' + (item.quantity - 1) + '">-</button>' +
                    '<span class="min-w-[26px] text-center text-[14px]">' + item.quantity + '</span>' +
                    '<button type="button" class="cursor-pointer px-2.5 py-1" data-line="' + (i + 1) + '" data-qty="' + (item.quantity + 1) + '">+</button>' +
                  '</span>') +
                '<span class="text-right">' +
                  (worth > item.final_line_price
                    ? '<s class="mr-1.5 text-[13px]" style="opacity:.45;">' + money(worth) + '</s>' : '') +
                  '<span class="text-[15px] font-bold">' + price + '</span>' +
                '</span>' +
              '</div>' +
            '</div>' +
          '</div>';
      }).join('');
    }

    function refresh(openAfter) {
      if (busy) return;
      busy = true;
      return fetch('/cart.js').then(function (r) { return r.json(); })
                .then(function (cart) {
          if (!cart.items) return fetch('/cart.js').then(function (r) { return r.json(); });
          return cart;
        })
        .then(function (cart) { render(cart); if (openAfter) open(); })
        .catch(function () { /* leave whatever is on screen rather than blanking it */ })
        .then(function () { busy = false; });
    }

    linesEl.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-qty],[data-remove]');
      if (!btn) return;
      var line = Number(btn.getAttribute('data-line'));

      if (btn.hasAttribute('data-remove')) {
        // By key, not by line: removing a bundle takes several lines at once
        // and the numbers would shift underneath.
        var drop = {};
        (btn.getAttribute('data-keys') || '').split(',').forEach(function (k) { if (k) drop[k] = 0; });
        api('/cart/update.js', { updates: drop }).then(function () { refresh(false); });
        return;
      }

      var qty = Number(btn.getAttribute('data-qty'));
      if (qty < 0) return;
      api('/cart/change.js', { line: line, quantity: qty }).then(function () { refresh(false); });
    });

    overlay.addEventListener('click', close);
    each(drawer, '[data-nova-drawer-close]', function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    // Anything that should open the drawer rather than navigate.
    each(document, '[data-nova-cart-open]', function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); refresh(true); });
    });

    // Add to cart posts through the drawer so the panel opens with the result.
    each(document, 'form[action*="/cart/add"]', function (form) {
      if (form.dataset.novaDrawerBound) return;
      form.dataset.novaDrawerBound = '1';
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var data = new FormData(form);
        fetch('/cart/add', { method: 'POST', body: data, headers: { Accept: 'application/json' } })
          .then(function () { refresh(true); })
          .catch(function () { form.submit(); });
      });
    });

    refresh(false);
  }

  /* ---- Popovers: a toggle button and a panel inside [data-nova-popover] --
     Only one is open at a time; a click outside or Escape closes it. */
  var openPopover = null;
  function setPopover(wrap, open) {
    var toggle = wrap.querySelector('[data-nova-popover-toggle]');
    var panel = wrap.querySelector('[data-nova-popover-panel]');
    if (!toggle || !panel) return;
    if (open && openPopover && openPopover !== wrap) setPopover(openPopover, false);
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    openPopover = open ? wrap : (openPopover === wrap ? null : openPopover);
  }
  function initPopovers(root) {
    each(root, '[data-nova-popover]', function (wrap) {
      if (wrap.dataset.novaReady) return;
      wrap.dataset.novaReady = '1';
      wrap.addEventListener('click', function (e) {
        if (e.target.closest('[data-nova-popover-toggle]')) {
          setPopover(wrap, wrap.querySelector('[data-nova-popover-panel]').hidden);
        } else if (e.target.closest('[data-nova-popover-close]')) {
          setPopover(wrap, false);
        }
      });
    });
    if (document.documentElement.dataset.novaPopoverReady) return;
    document.documentElement.dataset.novaPopoverReady = '1';
    document.addEventListener('click', function (e) {
      if (openPopover && !openPopover.contains(e.target)) setPopover(openPopover, false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openPopover) {
        var toggle = openPopover.querySelector('[data-nova-popover-toggle]');
        setPopover(openPopover, false);
        if (toggle) toggle.focus();
      }
    });
  }

  /* ---- Overlays: modal and side panel, opened by [data-nova-overlay-open] --
     Page scroll is locked while one is open; the backdrop, any
     [data-nova-overlay-close] and Escape close it and return focus. */
  var openOverlay = null;
  var overlayReturn = null;
  function setOverlay(overlay, open, opener) {
    if (!overlay) return;
    overlay.hidden = !open;
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) {
      openOverlay = overlay;
      overlayReturn = opener || null;
      var focusable = overlay.querySelector('[data-nova-overlay-close]');
      if (focusable) focusable.focus();
    } else {
      openOverlay = null;
      if (overlayReturn) overlayReturn.focus({ preventScroll: true });
      overlayReturn = null;
    }
  }
  function initOverlays(root) {
    each(root, '[data-nova-overlay]', function (overlay) {
      if (overlay.dataset.novaReady) return;
      overlay.dataset.novaReady = '1';
      // Lift out of the sticky column so it layers above everything. A copy
      // left behind by an earlier render in the theme editor is replaced.
      if (overlay.parentElement !== document.body) {
        var stale = overlay.id && document.querySelector('body > #' + CSS.escape(overlay.id));
        if (stale && stale !== overlay) stale.remove();
        document.body.appendChild(overlay);
      }
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || e.target.closest('[data-nova-overlay-close]')) setOverlay(overlay, false);
      });
    });
    if (document.documentElement.dataset.novaOverlayReady) return;
    document.documentElement.dataset.novaOverlayReady = '1';
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-nova-overlay-open]');
      if (!opener) return;
      if (openPopover) setPopover(openPopover, false);
      setOverlay(document.getElementById(opener.getAttribute('data-nova-overlay-open')), true, opener);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openOverlay) setOverlay(openOverlay, false);
    });
  }

  /* ---- Slides: one item at a time with prev/next and dot indicators ---- */
  function initSlides(root) {
    each(root, '[data-nova-slides]', function (wrap) {
      if (wrap.dataset.novaReady) return;
      wrap.dataset.novaReady = '1';
      var slides = wrap.querySelectorAll('[data-nova-slide]');
      var dots = wrap.querySelectorAll('[data-nova-slide-dot]');
      var index = 0;
      function show(i) {
        index = (i + slides.length) % slides.length;
        Array.prototype.forEach.call(slides, function (s, k) { s.hidden = k !== index; });
        Array.prototype.forEach.call(dots, function (d, k) { d.classList.toggle('is-active', k === index); });
      }
      wrap.addEventListener('click', function (e) {
        if (e.target.closest('[data-nova-slide-prev]')) show(index - 1);
        else if (e.target.closest('[data-nova-slide-next]')) show(index + 1);
      });
    });
  }

  /* ---- Bundle discount labels: state the saving the shopper actually gets --
     The bundle app's "% Off" tags are fixed text, so they stay the same when
     Subscribe & Save is ticked or unticked even though the price changes.
     Each tag is rewritten from the price and the struck-through price shown
     beside it, and kept in step as the app re-renders. */
  function initBundleLabels() {
    // Only on pages whose product section opts in (the original PDP keeps the app's tags).
    if (!document.querySelector('[data-nova-bundle-labels]')) return;
    if (document.documentElement.dataset.novaBundleLabels) return;
    document.documentElement.dataset.novaBundleLabels = '1';
    function amount(el) {
      if (!el) return NaN;
      return parseFloat(el.textContent.replace(/[^0-9.]/g, ''));
    }
    function relabel() {
      each(document, '.kaching-bundles__bar', function (bar) {
        var label = bar.querySelector('.kaching-bundles__bar-label');
        var price = amount(bar.querySelector('.kaching-bundles__bar-price'));
        var full = amount(bar.querySelector('.kaching-bundles__bar-full-price'));
        if (!label || !(price > 0) || !(full > price)) return;
        var text = Math.round((1 - price / full) * 100) + '% Off';
        if (label.textContent !== text) label.textContent = text;
      });
    }
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; relabel(); });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    relabel();
  }

  /* ---- Dispatch date: "Ships today" before the cut-off on a working day,
     otherwise the next working day, all reckoned in UK time so a visitor's own
     clock or time zone cannot move it. */
  function initDispatch(root) {
    each(root, '[data-nova-dispatch]', function (el) {
      var cutoff = parseInt(el.dataset.cutoff, 10) || 14;
      var holidays = (el.dataset.holidays || '').split(/[\s,]+/).filter(Boolean);
      function ukParts(date) {
        var parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23', weekday: 'short' })
          .formatToParts(date).forEach(function (p) { parts[p.type] = p.value; });
        return parts;
      }
      function isWorkingDay(p) {
        return p.weekday !== 'Sat' && p.weekday !== 'Sun' && holidays.indexOf(p.year + '-' + p.month + '-' + p.day) === -1;
      }
      function ordinal(n) {
        var s = ['th', 'st', 'nd', 'rd'], v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      function render() {
        var now = new Date();
        var p = ukParts(now);
        if (isWorkingDay(p) && parseInt(p.hour, 10) < cutoff) {
          el.textContent = el.dataset.today;
          return;
        }
        var day = new Date(now.getTime());
        for (var i = 0; i < 14; i++) {
          day = new Date(day.getTime() + 24 * 3600 * 1000);
          var q = ukParts(day);
          if (isWorkingDay(q)) {
            var month = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', month: 'long' }).format(day);
            el.textContent = el.dataset.later.replace('[date]', ordinal(parseInt(q.day, 10)) + ' ' + month);
            return;
          }
        }
      }
      render();
      if (!el.dataset.novaReady) {
        el.dataset.novaReady = '1';
        setInterval(render, 60000); // flips at the cut-off without a reload
      }
    });
  }

  /* ---- Live dispatch line with countdown (see snippets/ship-line). Same UK
     working-day rules as the trust-line date: weekends and listed holidays
     are skipped, and the visitor's own clock cannot move it. */
  function initShipLines(root) {
    each(root, '[data-nova-shipline]', function (el) {
      if (el.dataset.novaReady) return;
      el.dataset.novaReady = '1';
      var cutoff = parseInt(el.dataset.cutoff, 10) || 14;
      var holidays = (el.dataset.holidays || '').split(/[\s,]+/).filter(Boolean);
      var textEl = el.querySelector('[data-nova-shipline-text]');
      function uk(date) {
        var parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'long' })
          .formatToParts(date).forEach(function (p) { parts[p.type] = p.value; });
        return parts;
      }
      function working(p) {
        return p.weekday !== 'Saturday' && p.weekday !== 'Sunday' && holidays.indexOf(p.year + '-' + p.month + '-' + p.day) === -1;
      }
      function nextWorking(from) {
        var d = new Date(from.getTime());
        for (var i = 0; i < 14; i++) { d = new Date(d.getTime() + 864e5); if (working(uk(d))) return d; }
        return d;
      }
      function render() {
        var now = new Date(), p = uk(now);
        var h = parseInt(p.hour, 10), m = parseInt(p.minute, 10), sec = parseInt(p.second, 10);
        var html;
        if (working(p) && h < cutoff) {
          var left = (cutoff * 3600) - (h * 3600 + m * 60 + sec);
          var hh = Math.floor(left / 3600), mm = Math.floor((left % 3600) / 60);
          html = el.dataset.today.replace('[time]', (hh ? hh + 'h ' : '') + mm + 'm');
        } else {
          var ship = nextWorking(now), shipP = uk(ship);
          var tomorrow = uk(new Date(now.getTime() + 864e5));
          if (shipP.day === tomorrow.day && shipP.month === tomorrow.month) {
            html = el.dataset.later;
          } else {
            html = el.dataset.weekend.replace('[day]', shipP.weekday).replace('[next_day]', uk(nextWorking(ship)).weekday);
          }
        }
        if (textEl && textEl.innerHTML !== html) textEl.innerHTML = html;
      }
      render();
      setInterval(render, 1000);
    });
  }

  /* ---- Animated media outside the advertorial: stills first, then the
     looping video as it nears the viewport; reduced motion keeps the still. */
  /* ---- Sticky add to cart (see product-hero). Shown while the main button
     is out of view and the visitor is below it; its button presses the main
     one so the selected bundle, gift and protection are unchanged. */
  function initStickyAtc(root) {
    each(root, '[data-nova-sticky]', function (bar) {
      if (bar.dataset.novaReady) return;
      bar.dataset.novaReady = '1';
      var section = bar.closest('section') || document;
      var main = section.querySelector('#femalesProductForm button[name="add"], form button[name="add"]');
      if (!main) return;
      var priceEl = bar.querySelector('[data-nova-sticky-price]');
      var bundleEl = bar.querySelector('[data-nova-sticky-bundle]');
      var btn = bar.querySelector('[data-nova-sticky-add]');
      function syncPrice() {
        var sel = section.querySelector('.kaching-bundles__bar--selected');
        var price = sel && sel.querySelector('.kaching-bundles__bar-price');
        var title = sel && sel.querySelector('.kaching-bundles__bar-title');
        var p = price ? price.textContent.trim() : '';
        var t = title ? title.textContent.trim() : '';
        if (priceEl.textContent !== p) priceEl.textContent = p;
        var label = t ? t + (p ? ' ·' : '') : '';
        if (bundleEl.textContent !== label) bundleEl.textContent = label;
      }
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; syncPrice(); });
      }).observe(section, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
      syncPrice();
      function show(on) {
        bar.classList.toggle('is-shown', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        btn.tabIndex = on ? 0 : -1;
      }
      // Checked on scroll rather than by an observer, which misses a fast
      // flick that jumps straight past the button.
      var ticking = false;
      function check() {
        ticking = false;
        show(main.getBoundingClientRect().bottom < 0);
      }
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(check);
      }, { passive: true });
      window.addEventListener('resize', check);
      check();
      btn.addEventListener('click', function () { main.click(); });
    });
  }

  /* ---- Anonymous page stats (see workers/quiz-stats). Daily totals only:
     which product page, whether the visit came from an ad, a time bucket,
     how far down it got and whether a bundle or Add to Cart was tapped.
     No cookie, ID or answer is sent, and each thing counts once per visit. */
  function initPageStats() {
    var sec = document.querySelector('[data-nova-stats]');
    if (!sec || sec.dataset.novaStatsReady) return;
    sec.dataset.novaStatsReady = '1';
    var url = sec.dataset.novaStats.replace(/\/$/, '') + '/e';
    var page = sec.dataset.novaStatsPage;
    var src = /[?&](fbclid|ttclid|gclid)=|[?&]utm_medium=(paid|cpc)/i.test(location.search) ||
              /facebook|instagram|tiktok/i.test(document.referrer) ? 'ad' : 'other';
    var sent = {};
    function send(t, v) {
      var key = t + ':' + (v || '');
      if (sent[key]) return;
      sent[key] = true;
      var body = JSON.stringify({ t: t, p: page, src: src, v: v });
      try {
        if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
        else fetch(url, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
      } catch (e) {}
    }
    send('pdp_view');

    var t0 = Date.now(), timed = false;
    function sendTime() {
      // Once per visit: the first time the visitor leaves or switches away.
      if (timed) return;
      timed = true;
      var s = (Date.now() - t0) / 1000;
      send('pdp_time', s < 3 ? '0-3s' : s < 10 ? '3-10s' : s < 30 ? '10-30s' : s < 60 ? '30-60s' : s < 180 ? '1-3min' : '3min+');
    }
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') sendTime(); });
    window.addEventListener('pagehide', sendTime);

    var targets = { add_to_cart: '#femalesProductForm button[name="add"]', bundles: '.kaching-bundles__bar', reviews: '#reviews' };
    var ticking = false;
    function check() {
      ticking = false;
      var doc = document.documentElement;
      var pct = (window.scrollY + window.innerHeight) / Math.max(doc.scrollHeight, 1) * 100;
      [25, 50, 75, 100].forEach(function (d) { if (pct >= d - 1) send('pdp_scroll', String(d)); });
      Object.keys(targets).forEach(function (k) {
        var el = document.querySelector(targets[k]);
        if (el && el.getBoundingClientRect().top < window.innerHeight) send('pdp_reach', k);
      });
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }, { passive: true });
    setTimeout(check, 1500); // the bundle widget renders after load

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-nova-sticky-add]')) send('pdp_action', 'sticky_add_to_cart');
      else if (e.target.closest('#femalesProductForm button[name="add"]')) send('pdp_action', 'add_to_cart');
      else if (e.target.closest('.kaching-bundles__bar')) send('pdp_action', 'bundle_tap');
    }, true);
  }

  function initMediaAnims(root) {
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var vids = [];
    each(root, 'video[data-adv-anim]', function (v) {
      if (v.closest('[data-adv]') || v.dataset.novaReady) return; // the advertorial loads its own
      v.dataset.novaReady = '1';
      vids.push(v);
    });
    // Near the viewport: show the still (a lazy one waits until now), then
    // play, unless the visitor asked for reduced motion.
    function start(v) {
      if (v.dataset.poster && !v.getAttribute('poster')) v.setAttribute('poster', v.dataset.poster);
      if (still) return;
      v.querySelectorAll('source[data-src]').forEach(function (s) { s.src = s.getAttribute('data-src'); });
      v.load();
      var pr = v.play(); if (pr && pr.catch) pr.catch(function () {});
    }
    if (!('IntersectionObserver' in window)) { vids.forEach(start); return; }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { start(e.target); io.unobserve(e.target); } });
    }, { rootMargin: '600px 0px' });
    vids.forEach(function (v) { io.observe(v); });
  }

  function initAll(root) {
    initCartDrawer(root);
    initReviews(root);
    initCounters(root);
    initRestock(root);
    initCountdown(root);
    initAccordions(root);
    initTabs(root);
    initTimeline(root);
    initCarousels(root);
    initVideos(root);
    initReadMore(root);
    initGallery(root);
    initBundles(root);
    initFooterLinks(root);
    initNav(root);
    initPopovers(root);
    initOverlays(root);
    initSlides(root);
    initBundleLabels();
    initDispatch(root);
    initShipLines(root);
    initMediaAnims(root);
    initStickyAtc(root);
    initPageStats();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor lifecycle — re-run against the section that changed.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function () { initAll(document); });
  document.addEventListener('shopify:block:select', function (e) { initAll(e.target); });
})();
